/**
 * bd-share-basket.js
 * "Copy basket link" on the basket page and drawer, and the code that rebuilds
 * a basket from that link.
 *
 * Shopify ties a basket to the browser's cart cookie, so a plain /cart link
 * only ever shows the recipient their own basket. Shopify's own cart permalinks
 * carry line item properties for the first line only, which would drop cut
 * lists and specs. So the basket is copied whole, in one of two link forms:
 *
 *   /cart?bd-cart=<token>.<key>  short (about 100 characters): the basket is
 *     saved as its own cart through the Storefront API, which needs no access
 *     token on the shop's own domain. Shopify keeps unused carts 30 days.
 *   /cart?bd-basket=1z<data>     long, carrying the basket itself, used when the
 *     copy can't be saved. 1 = format version, z = deflate, j = plain JSON
 *     data = { c: created (unix seconds),
 *              i: [[variantId, quantity, properties|0, sellingPlanId|0, title], ...] }
 *     A configured washroom runs to several thousand characters, too long for
 *     some apps and for QR codes, hence the short form.
 *
 * Lines are kept oldest first: /cart.js lists newest first and a multi-item
 * add.js shows its last item first, so replaying oldest first keeps the order.
 *
 * The first links (2026-10-19) used /cart#bd-basket=. Email and chat apps can
 * drop a #hash on the way, which opened the recipient's own basket with no
 * prompt, so links now use the query string. Every form still opens. A script
 * at the top of layout/theme.liquid moves the query into the #hash before
 * analytics read the address, so baskets aren't logged.
 *
 * Opening a link:
 * - Links older than 14 days are refused. Configured items are price-point
 *   variants, so an old link would keep old prices.
 * - The whole basket is first test-added to a throwaway cookieless cart. A
 *   multi-item add.js is all-or-nothing, so if that fails each line is tested
 *   on its own to name the lines that can't be added. Their linked lines (same
 *   _shopisaw_group_id, or "Linked to" helpers) are left out with them.
 * - A non-empty basket is asked Replace / Add / Cancel. Replace adds first and
 *   then takes the old lines out, so a failed add never empties the basket.
 */
(function () {
  'use strict';

  var LINK_KEY = 'bd-basket=';
  var SHORT_KEY = 'bd-cart=';
  var LINK_QUERY = /[?&](bd-basket|bd-cart)=([^&#]*)/;
  var LINK_HASH = /(?:^#|&)(bd-basket|bd-cart)=([^&]*)/;
  var STOREFRONT_API = '/api/2026-10/graphql.json';
  var SNAP_KEY = 'bd-share-basket-snap';
  var SNAP_REUSE_MS = 24 * 60 * 60 * 1000;
  var VERSION = '1';
  var MAX_AGE_S = 14 * 24 * 60 * 60;
  var NOTICE_KEY = 'bd-share-basket-notice';
  var PROBE_CONCURRENCY = 4;
  var LIST_MAX = 6;
  var CONTACT_URL = '/pages/contact-us';

  function root() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* ---------- encoding ---------- */

  function bytesToB64url(bytes) {
    var s = '';
    for (var i = 0; i < bytes.length; i += 0x8000) {
      s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function b64urlToBytes(str) {
    var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    var bin = atob(b64);
    var out = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  function pipe(bytes, stream) {
    return new Response(new Blob([bytes]).stream().pipeThrough(stream))
      .arrayBuffer()
      .then(function (buf) {
        return new Uint8Array(buf);
      });
  }

  function encodePayload(payload) {
    var bytes = new TextEncoder().encode(JSON.stringify(payload));
    if (typeof CompressionStream === 'undefined') {
      return Promise.resolve(VERSION + 'j' + bytesToB64url(bytes));
    }
    return pipe(bytes, new CompressionStream('deflate')).then(function (z) {
      return VERSION + 'z' + bytesToB64url(z);
    });
  }

  function fail(code) {
    var e = new Error(code);
    e.code = code;
    return e;
  }

  function decodePayload(raw) {
    var str;
    try {
      str = decodeURIComponent(raw || '').replace(/\s+/g, '');
    } catch (e) {
      return Promise.reject(fail('invalid'));
    }
    if (str.charAt(0) !== VERSION) return Promise.reject(fail('invalid'));
    var mode = str.charAt(1);
    var bytes;
    try {
      bytes = b64urlToBytes(str.slice(2));
    } catch (e) {
      return Promise.reject(fail('invalid'));
    }
    var json;
    if (mode === 'j') {
      json = Promise.resolve(bytes);
    } else if (mode === 'z') {
      if (typeof DecompressionStream === 'undefined') return Promise.reject(fail('unsupported'));
      json = pipe(bytes, new DecompressionStream('deflate'));
    } else {
      return Promise.reject(fail('invalid'));
    }
    return json
      .then(function (b) {
        return validate(JSON.parse(new TextDecoder().decode(b)));
      })
      .catch(function (e) {
        throw e && e.code ? e : fail('invalid');
      });
  }

  function validate(data) {
    if (!data || typeof data.c !== 'number' || !Array.isArray(data.i) || !data.i.length) throw fail('invalid');
    var lines = data.i.map(function (l) {
      if (!Array.isArray(l)) throw fail('invalid');
      var id = Number(l[0]);
      var qty = Number(l[1]);
      var props = l[2] && typeof l[2] === 'object' && !Array.isArray(l[2]) ? l[2] : null;
      if (!(id > 0) || !(qty > 0) || Math.floor(qty) !== qty) throw fail('invalid');
      return { id: id, qty: qty, props: props, plan: Number(l[3]) || 0, title: String(l[4] || 'Item') };
    });
    return { created: data.c, lines: lines };
  }

  /* ---------- cart API ---------- */

  function readCart() {
    return fetch(root() + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  // Basket lines oldest first (cart.js lists newest first).
  function cartToLines(cart) {
    return cart.items
      .slice()
      .reverse()
      .map(function (it) {
        var plan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
        return {
          id: it.variant_id,
          qty: it.quantity,
          props: it.properties && Object.keys(it.properties).length ? it.properties : null,
          plan: (plan && plan.id) || 0,
          title: it.product_title || it.title || '',
        };
      });
  }

  function toItem(line) {
    var item = { id: line.id, quantity: line.qty };
    if (line.props && Object.keys(line.props).length) item.properties = line.props;
    if (line.plan) item.selling_plan = line.plan;
    return item;
  }

  // probe = true adds to a throwaway cart: no cookies go out and none come back.
  function postAdd(lines, probe) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      credentials: probe ? 'omit' : 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines.map(toItem) }),
    }).then(function (r) {
      return r
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
    });
  }

  function mapLimit(list, limit, fn) {
    var out = new Array(list.length);
    var next = 0;
    function worker() {
      if (next >= list.length) return Promise.resolve();
      var i = next++;
      return fn(list[i]).then(function (res) {
        out[i] = res;
        return worker();
      });
    }
    var workers = [];
    for (var w = 0; w < Math.min(limit, list.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () {
      return out;
    });
  }

  function addedTotal(res) {
    var items = (res && res.data && res.data.items) || [];
    return items.reduce(function (sum, it) {
      return sum + (Number(it.final_line_price) || 0);
    }, 0);
  }

  function isHelper(props) {
    if (!props) return false;
    return String(props._shopisaw_role || '').indexOf('helper') === 0 || props['Linked to'] != null;
  }

  // A line's group: its _shopisaw_group_id, else a "Linked to" helper joins the
  // line added just before it (lines are oldest first).
  function groupKeys(lines) {
    var last = null;
    return lines.map(function (l, i) {
      var p = l.props || {};
      if (p._shopisaw_group_id) return (last = 'g:' + p._shopisaw_group_id);
      if (p['Linked to'] != null && last) return last;
      return (last = 'i:' + i);
    });
  }

  function reasonFor(res) {
    var d = (res && res.data && (res.data.description || res.data.message)) || '';
    if (!d || /cannot find variant/i.test(d) || res.status === 404) return 'no longer available';
    return String(d);
  }

  function probeLines(lines) {
    return postAdd(lines, true).then(function (all) {
      if (all.ok) return { ok: lines, failed: [], linkedOut: 0, total: addedTotal(all) };
      if (all.status !== 422 && all.status !== 404) throw new Error('add.js ' + all.status);

      return mapLimit(lines, PROBE_CONCURRENCY, function (l) {
        return postAdd([l], true);
      }).then(function (results) {
        var keys = groupKeys(lines);
        var bad = {};
        results.forEach(function (r, i) {
          if (!r.ok) bad[keys[i]] = true;
        });
        var out = { ok: [], failed: [], linkedOut: 0, total: 0 };
        lines.forEach(function (l, i) {
          if (!bad[keys[i]]) {
            out.ok.push(l);
            out.total += addedTotal(results[i]);
          } else if (!results[i].ok) {
            out.failed.push({ line: l, reason: reasonFor(results[i]) });
          } else {
            out.linkedOut++;
          }
        });
        // Every line adds on its own but not together (e.g. a stock limit across
        // lines): report Shopify's message for the whole basket.
        if (!out.failed.length) {
          out.failed.push({ line: null, reason: reasonFor(all) });
          out.ok = [];
          out.total = 0;
        }
        return out;
      });
    });
  }

  function signature(variant, qty, props) {
    var p = props || {};
    var keys = Object.keys(p).sort();
    return variant + '|' + qty + '|' + keys.map(function (k) {
      return k + '=' + p[k];
    }).join('&');
  }

  function sameBasket(cart, lines) {
    if (!cart.items || cart.items.length !== lines.length) return false;
    var a = cart.items.map(function (it) {
      return signature(it.variant_id, it.quantity, it.properties);
    }).sort();
    var b = lines.map(function (l) {
      return signature(l.id, l.qty, l.props);
    }).sort();
    return a.join('\n') === b.join('\n');
  }

  function postUpdates(updates) {
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      if (!r.ok) throw new Error('update.js ' + r.status);
    });
  }

  function addOrThrow(lines) {
    return postAdd(lines, false).then(function (res) {
      if (!res.ok) throw new Error(reasonFor(res));
    });
  }

  // Replace = empty the basket, then add the shared lines, so the result is
  // exactly the shared basket in its order (adding first would merge identical
  // lines into the old ones). The shared lines were already test-added, but if
  // the add still fails the old lines are put back.
  function applyLines(lines, oldCart) {
    if (!oldCart || !oldCart.items.length) return addOrThrow(lines);
    var old = cartToLines(oldCart);
    var zero = {};
    oldCart.items.forEach(function (it) {
      zero[it.key] = 0;
    });
    return postUpdates(zero).then(function () {
      return addOrThrow(lines).catch(function (err) {
        return postAdd(old, false).then(
          function () {
            throw err;
          },
          function () {
            throw err;
          }
        );
      });
    });
  }

  /* ---------- short links (a saved copy of the basket) ---------- */

  // credentials: 'omit' keeps the copy apart from this visitor's own cart.
  function storefront(query, variables) {
    return fetch(STOREFRONT_API, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: query, variables: variables }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('storefront ' + r.status);
        return r.json();
      })
      .then(function (res) {
        if (res.errors && res.errors.length) throw new Error('storefront: ' + res.errors[0].message);
        return res.data;
      });
  }

  var SNAP_CREATE =
    'mutation($lines:[CartLineInput!]!){cartCreate(input:{lines:$lines}){cart{id totalQuantity}userErrors{message}warnings{message}}}';
  var SNAP_READ =
    'query($id:ID!,$after:String){cart(id:$id){createdAt lines(first:250,after:$after){pageInfo{hasNextPage endCursor}' +
    'nodes{quantity attributes{key value}sellingPlanAllocation{sellingPlan{id}}merchandise{...on ProductVariant{id product{title}}}}}}}';

  function gidNumber(gid) {
    return Number(String(gid || '').split('/').pop()) || 0;
  }

  // Saves the lines as a cart of their own; resolves with "<token>.<key>".
  function saveSnapshot(lines) {
    var want = 0;
    var input = lines.map(function (l) {
      want += l.qty;
      var line = { merchandiseId: 'gid://shopify/ProductVariant/' + l.id, quantity: l.qty };
      var props = l.props || {};
      var attrs = Object.keys(props)
        .filter(function (k) {
          return props[k] != null;
        })
        .map(function (k) {
          return { key: k, value: String(props[k]) };
        });
      if (attrs.length) line.attributes = attrs;
      if (l.plan) line.sellingPlanId = 'gid://shopify/SellingPlan/' + l.plan;
      return line;
    });
    return storefront(SNAP_CREATE, { lines: input }).then(function (d) {
      var res = d && d.cartCreate;
      var c = res && res.cart;
      // Anything Shopify changed or left out (stock, limits) means the copy isn't this basket.
      if (!c || res.userErrors.length || res.warnings.length || c.totalQuantity !== want) {
        throw new Error('snapshot differs from the basket');
      }
      var m = /\/Cart\/([A-Za-z0-9_-]+)\?key=([A-Za-z0-9_-]+)$/.exec(c.id);
      if (!m) throw new Error('snapshot id ' + c.id);
      return m[1] + '.' + m[2];
    });
  }

  function readSnapshot(raw) {
    var m = /^([A-Za-z0-9_-]+)\.([A-Za-z0-9_-]+)$/.exec(String(raw || '').replace(/\s+/g, ''));
    if (!m) return Promise.reject(fail('invalid'));
    var id = 'gid://shopify/Cart/' + m[1] + '?key=' + m[2];
    var lines = [];
    var created = 0;
    function page(after) {
      return storefront(SNAP_READ, { id: id, after: after || null }).then(function (d) {
        var c = d && d.cart;
        if (!c) throw fail('gone');
        created = Date.parse(c.createdAt) / 1000;
        c.lines.nodes.forEach(function (n) {
          var props = {};
          n.attributes.forEach(function (a) {
            props[a.key] = a.value;
          });
          var plan = n.sellingPlanAllocation && n.sellingPlanAllocation.sellingPlan;
          var v = n.merchandise;
          // Shopify takes deleted variants out of carts, so this is only a guard.
          if (!v) return;
          lines.push({
            id: gidNumber(v.id),
            qty: n.quantity,
            props: n.attributes.length ? props : null,
            plan: plan ? gidNumber(plan.id) : 0,
            title: (v.product && v.product.title) || 'Item',
          });
        });
        if (c.lines.pageInfo.hasNextPage) return page(c.lines.pageInfo.endCursor);
      });
    }
    return page().then(function () {
      if (!lines.length) throw fail('gone');
      // Carts list newest first, as cart.js does.
      return { created: created, lines: lines.reverse() };
    });
  }

  function linkSignature(lines) {
    return JSON.stringify(
      lines.map(function (l) {
        return [l.id, l.qty, l.props, l.plan];
      })
    );
  }

  // Copying the same basket again, or each time the drawer opens on a phone,
  // reuses its saved copy for a day instead of saving another.
  var snapPending = {};

  function shortLink(lines) {
    var sig = linkSignature(lines);
    var token = null;
    try {
      var saved = JSON.parse(sessionStorage.getItem(SNAP_KEY) || 'null');
      if (saved && saved.sig === sig && Date.now() - saved.t < SNAP_REUSE_MS) token = saved.token;
    } catch (e) {}
    var job = token
      ? Promise.resolve(token)
      : snapPending[sig] ||
        (snapPending[sig] = saveSnapshot(lines).then(
          function (t) {
            delete snapPending[sig];
            try {
              sessionStorage.setItem(SNAP_KEY, JSON.stringify({ sig: sig, token: t, t: Date.now() }));
            } catch (e) {}
            return t;
          },
          function (e) {
            delete snapPending[sig];
            throw e;
          }
        ));
    return job.then(function (t) {
      return location.origin + root() + 'cart?' + SHORT_KEY + t;
    });
  }

  function longLink(lines) {
    var rows = lines.map(function (l) {
      return [l.id, l.qty, l.props || 0, l.plan, l.title];
    });
    return encodePayload({ c: Math.floor(Date.now() / 1000), i: rows }).then(function (data) {
      return location.origin + root() + 'cart?' + LINK_KEY + data;
    });
  }

  /* ---------- UI ---------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function money(pence) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
    } catch (e) {
      return '£' + (pence / 100).toFixed(2);
    }
  }

  var dialog = null;
  var settleActive = null;

  function getDialog() {
    if (dialog) return dialog;
    dialog = el('dialog', 'bd-sb-dialog');
    dialog.setAttribute('aria-labelledby', 'bd-sb-dialog-title');
    document.body.appendChild(dialog);
    return dialog;
  }

  function closeDialog() {
    if (dialog && dialog.open) dialog.close();
  }

  // Shows the dialog. Resolves with the chosen action value, or null on Cancel / Esc.
  // With no actions it is a progress dialog that Esc can't close.
  function openDialog(title, body, actions) {
    var d = getDialog();
    if (settleActive) settleActive();
    d.innerHTML = '';
    var h = el('h2', 'bd-sb-dialog__title', title);
    h.id = 'bd-sb-dialog-title';
    d.appendChild(h);
    body.forEach(function (node) {
      d.appendChild(node);
    });
    d.oncancel = actions && actions.length ? null : function (e) {
      e.preventDefault();
    };
    return new Promise(function (resolve) {
      var done = false;
      // replaced = a newer step took over the dialog, so leave it open.
      function finish(v, replaced) {
        if (done) return;
        done = true;
        d.removeEventListener('close', onClose);
        if (settleActive === settle) settleActive = null;
        if (!replaced) closeDialog();
        resolve(v);
      }
      function settle() {
        finish(null, true);
      }
      settleActive = settle;
      // dialog.close() fires 'close' a task later, so the close of the previous
      // step can arrive after this step has opened. Ignore it while still open.
      function onClose() {
        if (!d.open) finish(null);
      }
      if (actions && actions.length) {
        var row = el('div', 'bd-sb-dialog__actions');
        actions.forEach(function (a) {
          var cls = 'bd-sb-dialog__btn' + (a.primary ? ' bd-sb-dialog__btn--primary' : '');
          if (a.plain) cls += ' bd-sb-dialog__btn--plain';
          var b = el('button', cls, a.label);
          b.type = 'button';
          b.addEventListener('click', function () {
            finish(a.value);
          });
          row.appendChild(b);
        });
        d.appendChild(row);
      }
      d.addEventListener('close', onClose);
      if (!d.open) {
        if (typeof d.showModal === 'function') d.showModal();
        else d.setAttribute('open', '');
      }
    });
  }

  function showBusy(text) {
    openDialog(text, [el('p', 'bd-sb-dialog__text', 'This only takes a moment.')], []);
  }

  function message(title, text, withContact) {
    var body = [el('p', 'bd-sb-dialog__text', text)];
    if (withContact) {
      var p = el('p', 'bd-sb-dialog__text');
      var a = el('a', null, 'Contact us');
      a.href = CONTACT_URL;
      p.appendChild(a);
      p.appendChild(document.createTextNode(' and we can build the basket for you.'));
      body.push(p);
    }
    return openDialog(title, body, [{ label: 'Close', value: 'close', primary: true }]);
  }

  var DECODE_ERRORS = {
    unsupported: [
      "Your browser can't open this link",
      'Please update your browser, or open the link in a recent version of Chrome, Edge, Safari or Firefox.',
    ],
    invalid: [
      "This basket link doesn't work",
      'The link looks incomplete. It may have been cut short when it was sent. Ask the person who sent it to copy the link again.',
    ],
    gone: [
      'This basket link has expired',
      'The shared basket is no longer saved. Ask the person who sent it to share their basket again.',
    ],
  };

  function itemList(lines) {
    var main = lines.filter(function (l) {
      return !isHelper(l.props);
    });
    var ul = el('ul', 'bd-sb-dialog__list');
    main.slice(0, LIST_MAX).forEach(function (l) {
      ul.appendChild(el('li', null, l.title + (l.qty > 1 ? ' × ' + l.qty : '')));
    });
    if (main.length > LIST_MAX) ul.appendChild(el('li', 'bd-sb-dialog__more', '+ ' + (main.length - LIST_MAX) + ' more'));
    return ul;
  }

  function failedBlock(probe) {
    var nodes = [el('p', 'bd-sb-dialog__label', "Can't be added")];
    var ul = el('ul', 'bd-sb-dialog__list bd-sb-dialog__list--failed');
    probe.failed.forEach(function (f) {
      ul.appendChild(el('li', null, f.line ? f.line.title + ': ' + f.reason : f.reason));
    });
    nodes.push(ul);
    if (probe.linkedOut) {
      nodes.push(el('p', 'bd-sb-dialog__text bd-sb-dialog__text--muted', 'Their linked cutting and edging lines are left out too.'));
    }
    return nodes;
  }

  function askChoice(probe, cartHasItems) {
    var body = [];
    var mainCount = probe.ok.filter(function (l) {
      return !isHelper(l.props);
    }).length;
    var summary = mainCount + (mainCount === 1 ? ' item' : ' items');
    if (probe.total) summary += ' · ' + money(probe.total) + ' exc VAT';
    body.push(el('p', 'bd-sb-dialog__summary', summary));
    body.push(itemList(probe.ok));
    if (probe.failed.length) body = body.concat(failedBlock(probe));

    var actions;
    if (cartHasItems) {
      body.push(el('p', 'bd-sb-dialog__text', 'Your basket already has items in it.'));
      actions = [
        { label: 'Replace my basket', value: 'replace', primary: true },
        { label: 'Add to my basket', value: 'add' },
        { label: 'Cancel', value: null, plain: true },
      ];
    } else {
      actions = [
        { label: 'Add available items', value: 'add', primary: true },
        { label: 'Cancel', value: null, plain: true },
      ];
    }
    return openDialog('Shared basket', body, actions);
  }

  /* ---------- restore ---------- */

  // Takes the link off the address (query or #hash), keeping any other query.
  function stripLink() {
    var search = location.search.replace(LINK_QUERY, '').replace(/^&/, '?');
    try {
      history.replaceState(history.state, '', location.pathname + search);
    } catch (e) {
      location.hash = '';
    }
  }

  // load() resolves with { created, lines }, decoded from a long link or read
  // back from the saved copy a short link names.
  function restore(load) {
    stripLink();
    var payload;
    var cart;
    var probe;
    showBusy('Opening shared basket…');
    return load()
      .then(function (p) {
        payload = p;
        var age = Date.now() / 1000 - payload.created;
        if (age > MAX_AGE_S) throw fail('expired');
        return readCart();
      })
      .then(function (c) {
        cart = c;
        if (cart.items.length && sameBasket(cart, payload.lines)) throw fail('same');
        return probeLines(payload.lines);
      })
      .then(function (pr) {
        probe = pr;
        closeDialog();
        if (!probe.ok.length) throw fail('none');
        if (!cart.items.length && !probe.failed.length) return 'add';
        return askChoice(probe, cart.items.length > 0);
      })
      .then(function (mode) {
        if (!mode) return;
        showBusy('Adding shared basket…');
        return applyLines(probe.ok, mode === 'replace' ? cart : null).then(function () {
          try {
            sessionStorage.setItem(
              NOTICE_KEY,
              JSON.stringify({
                added: probe.ok.filter(function (l) {
                  return !isHelper(l.props);
                }).length,
                skipped: probe.failed.length,
              })
            );
          } catch (e) {}
          location.replace(root() + 'cart');
        });
      })
      .catch(function (e) {
        closeDialog();
        var code = e && e.code;
        if (DECODE_ERRORS[code]) return message(DECODE_ERRORS[code][0], DECODE_ERRORS[code][1]);
        if (code === 'expired') {
          return message(
            'This basket link has expired',
            'Shared basket links work for 14 days so the prices stay current. Ask the person who sent it to share their basket again.'
          );
        }
        if (code === 'same') return showNotice('This shared basket is already in your basket.');
        if (code === 'none') {
          return openDialog('Shared basket', failedBlock(probe), [{ label: 'Close', value: 'close', primary: true }]);
        }
        console.warn('BD share basket: restore failed', e);
        return message(
          "We couldn't add the shared basket",
          'Something went wrong while adding the items. Please try the link again.',
          true
        );
      });
  }

  function showNotice(text) {
    var anchor = document.querySelector('cart-items') || document.getElementById('MainContent');
    if (!anchor) return;
    var n = el('div', 'bd-sb-notice page-width');
    n.setAttribute('role', 'status');
    n.appendChild(el('p', 'bd-sb-notice__text', text));
    if (anchor.tagName === 'CART-ITEMS') anchor.parentNode.insertBefore(n, anchor);
    else anchor.insertBefore(n, anchor.firstChild);
  }

  function showStoredNotice() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(NOTICE_KEY);
      if (raw) sessionStorage.removeItem(NOTICE_KEY);
    } catch (e) {}
    if (!raw) return;
    var n;
    try {
      n = JSON.parse(raw);
    } catch (e) {
      return;
    }
    var text = 'Shared basket added: ' + n.added + (n.added === 1 ? ' item.' : ' items.');
    if (n.skipped) text += ' ' + n.skipped + (n.skipped === 1 ? " item couldn't" : " items couldn't") + ' be added.';
    showNotice(text);
  }

  /* ---------- copy / share ---------- */

  function canShare() {
    return !!(navigator.share && window.matchMedia && matchMedia('(pointer: coarse)').matches);
  }

  // long = true skips the saved copy (the fallback, and for tests).
  function buildLink(long) {
    return readCart().then(function (cart) {
      if (!cart.items || !cart.items.length) throw fail('empty');
      var lines = cartToLines(cart);
      if (long) return longLink(lines);
      return shortLink(lines).catch(function (e) {
        console.warn('BD share basket: short link failed, using a long one', e);
        return longLink(lines);
      });
    });
  }

  // Request a quote on a product page (bd-quote-request.js): a link that opens a design as a
  // basket without it going in this browser's basket. items are cart/add.js items in the order
  // they'd be added, properties as an object.
  function linkForItems(items) {
    var lines = items.map(function (it) {
      var props = it.properties && Object.keys(it.properties).length ? it.properties : null;
      return { id: Number(it.id), qty: Number(it.quantity) || 1, props: props, plan: 0, title: it.title || '' };
    });
    if (!lines.length) return Promise.reject(fail('empty'));
    return shortLink(lines).catch(function (e) {
      console.warn('BD share basket: short link failed, using a long one', e);
      return longLink(lines);
    });
  }

  // Phones share through the share sheet, which Safari only opens straight from
  // the tap. So on phones the link is built in advance: when the basket page
  // loads, when the drawer opens and whenever either re-renders after a change.
  var prepared = { url: null, run: 0 };

  function prepare() {
    var run = ++prepared.run;
    prepared.url = null;
    buildLink()
      .then(function (url) {
        if (run === prepared.run) prepared.url = url;
      })
      .catch(function () {});
  }

  // Safari also only lets the clipboard be written straight from the tap; a
  // ClipboardItem holding a promise is the way it allows waiting for the link.
  function copyLink(urlPromise) {
    if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
      try {
        var blob = urlPromise.then(function (u) {
          return new Blob([u], { type: 'text/plain' });
        });
        return navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]).catch(function () {
          return writeText(urlPromise);
        });
      } catch (e) {}
    }
    return writeText(urlPromise);
  }

  function writeText(urlPromise) {
    return urlPromise.then(function (u) {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('no clipboard');
      return navigator.clipboard.writeText(u);
    });
  }

  function setStatus(wrap, text) {
    var s = wrap.querySelector('[data-bd-share-basket-status]');
    if (s) s.textContent = text;
  }

  function showManualCopy(wrap, url) {
    var box = wrap.querySelector('.bd-share-basket__manual');
    if (!box) {
      box = el('input', 'bd-share-basket__manual');
      box.type = 'text';
      box.readOnly = true;
      box.setAttribute('aria-label', 'Basket link');
      wrap.appendChild(box);
    }
    box.value = url;
    box.focus();
    box.select();
  }

  var COPIED = 'Link copied. Anyone you send it to can open this basket for the next 14 days.';

  function onShareClick(btn) {
    var wrap = btn.closest('[data-bd-share-basket]') || btn.parentNode;
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    setStatus(wrap, '');

    var urlPromise;
    var shareUrl = canShare() && prepared.url;
    var job;
    if (shareUrl) {
      urlPromise = Promise.resolve(shareUrl);
      job = navigator.share({ title: 'Bridgewater Direct basket', url: shareUrl }).then(
        function () {
          setStatus(wrap, 'Link shared. It opens this basket for the next 14 days.');
        },
        function (e) {
          if (e && e.name === 'AbortError') return;
          return copyLink(urlPromise).then(function () {
            setStatus(wrap, COPIED);
          });
        }
      );
    } else {
      urlPromise = buildLink();
      job = copyLink(urlPromise).then(function () {
        setStatus(wrap, COPIED);
      });
    }

    job
      .catch(function () {
        return urlPromise.then(function (url) {
          showManualCopy(wrap, url);
          setStatus(wrap, 'Copy this link. It opens this basket for the next 14 days.');
        });
      })
      .catch(function (e) {
        setStatus(
          wrap,
          e && e.code === 'empty' ? 'Your basket is empty.' : "Sorry, the link couldn't be made. Please try again."
        );
      })
      .then(function () {
        btn.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bd-share-basket-btn]');
    if (!btn) return;
    e.preventDefault();
    onShareClick(btn);
  });

  var SHARE_LABEL = 'Share basket link';

  function relabel() {
    document.querySelectorAll('[data-bd-share-basket-label]').forEach(function (s) {
      if (s.textContent !== SHARE_LABEL) s.textContent = SHARE_LABEL;
    });
  }

  // True when a re-render put a new share button on the page.
  function addedButton(records) {
    return records.some(function (r) {
      return Array.prototype.some.call(r.addedNodes, function (n) {
        return (
          n.nodeType === 1 &&
          (n.matches('[data-bd-share-basket-btn]') || !!n.querySelector('[data-bd-share-basket-btn]'))
        );
      });
    });
  }

  function watchForSharing() {
    if (!canShare()) return;
    relabel();
    var drawer = document.querySelector('cart-drawer');
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.querySelector('[data-bd-share-basket-btn]')) prepare();
    if (!window.MutationObserver) return;
    if (drawer) {
      var wasActive = drawer.classList.contains('active');
      new MutationObserver(function () {
        var active = drawer.classList.contains('active');
        if (active && !wasActive) prepare();
        wasActive = active;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      new MutationObserver(function (records) {
        if (!addedButton(records)) return;
        relabel();
        if (drawer.classList.contains('active')) prepare();
      }).observe(drawer, { childList: true, subtree: true });
    }
    if (footer) {
      new MutationObserver(function (records) {
        if (!addedButton(records)) return;
        relabel();
        prepare();
      }).observe(footer, { childList: true, subtree: true });
    }
  }

  // The theme.liquid head script has normally moved the link into the #hash
  // already; the query is read too in case it couldn't.
  function restoreFromLink() {
    var m = (location.hash || '').match(LINK_HASH) || location.search.match(LINK_QUERY);
    if (!m) return;
    var raw = m[2];
    restore(function () {
      return m[1] === 'bd-cart' ? readSnapshot(raw) : decodePayload(raw);
    });
  }

  function init() {
    watchForSharing();
    showStoredNotice();
    restoreFromLink();
    // An old #link pasted into a tab already on /cart only changes the hash.
    window.addEventListener('hashchange', restoreFromLink);
  }

  window.BDShareBasket = { encode: encodePayload, decode: decodePayload, buildLink: buildLink, linkFor: linkForItems };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
